//region :: Imports ::
import React, {createRef, ReactElement, RefObject} from 'react';
import {withMsal, WithMsalProps} from '@azure/msal-react';
import MForm from '../../Toolkit/Components/Controls/Form';
import MPage, {MPageProps} from '../../Toolkit/Components/Shell/Page';
import MToolbar from '../../Toolkit/Components/Controls/Toolbar';
import MComponent from '../../Toolkit/Components/Component';
import MSpacer from '../../Toolkit/Components/Controls/Spacer';
import MFormSection from '../../Toolkit/Components/Controls/FormSection';
import {MFormOutcome} from '../../Toolkit/Components/Controls/Form';
import WithRouterProps from '../../Toolkit/WithRouterProps';
import withRouter from '../../Toolkit/WithRouter';
import {FieldProps, FormikHelpers} from 'formik';
import * as Yup from 'yup';
import NSwagAdapter from '../../Toolkit/Api/NSwagAdapter';
import MNSwagClientConfiguration from '../../Toolkit/Api/NSwagClientConfiguration';
import TrafficManagementAccessTokenProvider from '../../Api/TrafficManagementAccessTokenProvider';
import {toErrorMessage} from '../../Toolkit/Utils';
import MAlertDialog from '../../Toolkit/Components/Controls/AlertDialog';
import MFormFieldControl from '../../Toolkit/Components/Controls/FormFieldControl';
import MTextBox from '../../Toolkit/Components/Controls/TextBox';
import MDropDown from '../../Toolkit/Components/Controls/DropDown';
import MSaveButton from '../../Toolkit/Components/Controls/SaveButton';
import {CrewClient, UpdateCrewRequest} from '../../Api/CrewClient';
import {CreateCrewValidator, Crew, CrewDataReply} from '../../Api/ControllerTypes/CrewTypes';
import MBackButton from '../../Toolkit/Components/Controls/BackButton';
import ContactListControl from '../../Components/ContactListControl';
import CrewJobsControl from '../../Components/CrewJobsControl';
import EnumContext from '../../Context/enum-context';
import {ColorPicker} from 'primereact/colorpicker';
import MButton from '../../Toolkit/Components/Controls/Button';
import {MNewIconName} from '../../Toolkit/Icons';

//endregion

/** */
type RouteParams = {
    id: string;
};

/** */
enum PageActions {
    None = 0,
    Saving = 1
}

/** */
type Props = WithMsalProps & WithRouterProps<RouteParams> & {}

/** */
type State = {
    pageTitle: string;
    pageSubtitle: string;
    page: MPageProps;
    form: {
        id: string;
        data: Crew;
        validationSchema: any;
    }
    pageActions: PageActions;
}

/** */
class CreateCrewPage extends MComponent<Props, State> {
    static contextType = EnumContext;
    context!: React.ContextType<typeof EnumContext>

    private readonly m_routeParams: RouteParams;
    private readonly m_pageRef: RefObject<MPage>;
    private readonly m_formRef: RefObject<MForm<FormData>>;
    private readonly m_crewClient: NSwagAdapter<CrewClient>;
    private readonly m_alertDialogRef: RefObject<MAlertDialog>;
    
    /** */
    public constructor(props: Props) {
        super(props);

        this.bindMethods();

        this.m_routeParams = this.props.match.params;
        this.m_pageRef = createRef<MPage>();
        this.m_formRef = createRef<MForm<FormData>>();
        this.m_crewClient = new NSwagAdapter<CrewClient>(new CrewClient(
            new MNSwagClientConfiguration(process.env.REACT_APP_TRAFFICMANAGEMENT_API_KEY!,
                new TrafficManagementAccessTokenProvider(this.props.msalContext))));
        this.m_alertDialogRef = createRef<MAlertDialog>();

        this.initializeState();

    }

    /** */
    private bindMethods(): void {

        this.getToolbarTemplate = this.getToolbarTemplate.bind(this);
        this.close_Click = this.close_Click.bind(this);
        this.activate_Click = this.activate_Click.bind(this);
        this.deactivate_Click = this.deactivate_Click.bind(this);
        this.form_onBeforeSubmit = this.form_onBeforeSubmit.bind(this);
        this.form_onSubmit = this.form_onSubmit.bind(this);
        this.form_onAfterSubmit = this.form_onAfterSubmit.bind(this);
        this.form_onError = this.form_onError.bind(this);
        this.page_onLoadData = this.page_onLoadData.bind(this);
        this.setPrimaryRequest = this.setPrimaryRequest.bind(this);
        this.isEditDisabled = this.isEditDisabled.bind(this);
        this.isActivateDisabled = this.isActivateDisabled.bind(this);
        this.isDeactivateDisabled = this.isDeactivateDisabled.bind(this);

    }

    /** */
    private initializeState(): void {
        Yup.addMethod(Yup.string, 'checkDuplicate', function validateName() {
            return this.test('crewname-duplicate', 'Crew name must be unique', function(value) {
                return new Promise((resolve, reject) => {
                    if(value === 'Test') {
                        reject('Crew name must be unique');
                    }
                    resolve(true);
                });
            });
        });
        //  Page
        const page: MPageProps = {locked: false};

        const data: Crew = {
            id: 0,
            crewName: '',
            crewType: 0,
            workArea: '',
            assignedJobs: [],
            highlightColour: '',
            active: true
        };

        const validationSchema: any = Yup.object().shape({
            crewName: CreateCrewValidator.crewNameFunc(this.m_crewClient, false),
            crewType: CreateCrewValidator.crewTypeFunc(this.m_crewClient, false),
            workArea: CreateCrewValidator.workArea
        });
        
        this.state = {
            pageTitle: '',
            pageSubtitle: '',
            page: page,
            form: {
                id: 'editCrewForm',
                data: data,
                validationSchema: validationSchema,
            },
            pageActions: PageActions.None
        };

    }

    private isEditDisabled(): boolean {
        const allowedRoles: string[] = ['Booking', 'Supervisor'];
        const roles = this.props.msalContext.accounts[0].idTokenClaims!.roles;
        const access = allowedRoles.some(a => roles?.includes(a));

        return !access
            || this.isFormDisabled;
    }

    private isActivateDisabled(): boolean {
        const roles = this.props.msalContext.accounts[0].idTokenClaims!.roles;
        const supervisorMember = roles?.includes('Supervisor') === true;

        if(!this.state.form.data.active && supervisorMember) {
            return false;
        }
        return true;
    }

    private isDeactivateDisabled(): boolean {
        const roles = this.props.msalContext.accounts[0].idTokenClaims!.roles;
        const supervisorMember = roles?.includes('Supervisor') === true;

        if(this.state.form.data.active && supervisorMember) {
            return false;
        }
        return true;
    }

    public componentDidMount(): void {
    }

    private form_onBeforeSubmit(fieldValues: Crew, formikHelpers: FormikHelpers<Crew>): boolean {
        this.safeSetState(draft => draft.pageActions = PageActions.Saving);
        return false;
    }

    private async form_onSubmit(fieldValues: Crew, formikHelpers: FormikHelpers<Crew>): Promise<void> {
        const updateRequest = new UpdateCrewRequest({
            id: fieldValues.id,
            crewName: fieldValues.crewName,
            crewType: fieldValues.crewType,
            workArea: fieldValues.workArea,
            highlightColour: fieldValues.highlightColour
        });

        const result = await this.m_crewClient.callWithReply(CrewDataReply, (client => client.update(updateRequest)));
        result.throwIfFailed()
    }

    private form_onAfterSubmit(outcome: MFormOutcome, fieldValues: Crew, formikHelpers: FormikHelpers<Crew>, exception: any | undefined): void {
        if(outcome === 'success') {
            this.m_pageRef.current!.messageBar.success(`Crew entry update successful.`);
        }

        this.safeSetState((draft: State) => {

            draft.pageActions &= ~PageActions.Saving;
            draft.pageActions = PageActions.None;

        });
    }

    private form_onError(exception: any): boolean
    {
        const handled = false;
        this.m_pageRef.current!.messageBar.error(toErrorMessage(exception));
        return handled;
    }

    private async page_onLoadData(): Promise<void> {
        const id = Number(this.props.match.params.id);

        //Pull crew data
        const result = await this.m_crewClient.callWithReply(CrewDataReply, (client) => client.get(id));
        result.throwIfFailed();

        this.safeSetState((draft) => {
            draft.form.data = result.reply!.crew!.crew;
            draft.pageTitle = 'Edit Crew';
        });
    }

    /** */
    private getToolbarTemplate(): ReactElement<MToolbar> {

        return (
            <MToolbar>
                <MBackButton disabled={false}/>

                <MSpacer/>
                <MSaveButton formId={this.state.form.id}
                             iconOnly={false}
                             isBusy={this.isSaving}
                             disabled={this.isFormDisabled}/>
                <MSpacer/>
                <MButton label="Activate"
                         icon={MNewIconName}
                         iconOnly={false}
                         disabled={this.isActivateDisabled}
                         onClick={this.activate_Click}/>
                <MSpacer/>
                <MButton label="Deactivate"
                         icon={MNewIconName}
                         color={'danger'}
                         iconOnly={false}
                         disabled={this.isDeactivateDisabled}
                         onClick={this.deactivate_Click}/>
                <MSpacer/>
            </MToolbar>
        );

    }

    private async close_Click(): Promise<void> {
        this.props.history.goBack();
    }

    private async activate_Click(): Promise<void> {
        const result = await this.m_crewClient.call(
            (client => client.activate(this.state.form.data.id)));
        result.throwIfFailed()

        this.page_onLoadData()
    }

    private async deactivate_Click(): Promise<void> {
        const result = await this.m_crewClient.call(
            (client => client.deactivate(this.state.form.data.id)));
        result.throwIfFailed()

        this.page_onLoadData()
    }

    private async setPrimaryRequest(id: number) : Promise<void> {
        const updateRequest = new UpdateCrewRequest({
            id: this.state.form.data.id,
            crewName: this.state.form.data.crewName,
            crewType: this.state.form.data.crewType,
            workArea: this.state.form.data.workArea,
            primaryContact: id
        });
        const result = await this.m_crewClient.callWithReply(CrewDataReply, (client) => client.update(updateRequest));
        result.throwIfFailed();

        await this.page_onLoadData()
    }

    /** */
    public render(): ReactElement {

        return (
            <MPage title={this.state.pageTitle}
                   subtitle={this.state.pageSubtitle}
                   locked={this.state.page.locked}
                   width={12}
                   toolbar={this.getToolbarTemplate()}
                   onLoadData={this.page_onLoadData}
                   ref={this.m_pageRef}>

                <MAlertDialog ref={this.m_alertDialogRef}/>

                <MForm formId={this.state.form.id}
                       initialValues={this.state.form.data}
                       validationSchema={this.state.form.validationSchema}
                       onBeforeSubmit={this.form_onBeforeSubmit}
                       onSubmit={this.form_onSubmit}
                       onAfterSubmit={this.form_onAfterSubmit}
                       onError={this.form_onError}>

                    <div className="formgrid grid">
                        <div className="col-12">

                            <MFormSection title="General">
                                <div className="col-12 md:col-4">
                                    <MFormFieldControl label="Crew Name"
                                                       fieldName="crewName">

                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {

                                            return (
                                                <MTextBox id={fieldProps.field.name}
                                                          maxLength={Crew.CrewNameMaxLength}
                                                          readonly={false}
                                                          fieldProps={fieldProps}
                                                          disabled={this.isEditDisabled}
                                                          className={fieldClasses}/>
                                            );

                                        }}

                                    </MFormFieldControl>
                                </div>

                                <div className="col-12 md:col-3">
                                    <MFormFieldControl label={'Crew Type'}
                                                       fieldName={'crewType'}>
                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                            return (
                                                <MDropDown<number> id="CrewType"
                                                                   disabled={this.isEditDisabled}
                                                                   fieldProps={fieldProps}
                                                                   items={this.context?.enums === null ? [] : this.context?.enums.crewTypes.filter(p => p.id >= 0)
                                                                              .map((enumvalue) => {
                                                                                  return {label: enumvalue.name, value: enumvalue.id};
                                                                              })}
                                                                   initialValue={0}
                                                />
                                            );
                                        }}
                                    </MFormFieldControl>
                                </div>

                                <div className="col-12 md:col-1">
                                    <MFormFieldControl label={'Colour'} fieldName={'highlightColour'}>
                                        {(fieldProps: FieldProps, fieldClasses: string | undefined) => {
                                            return (
                                                <ColorPicker value={fieldProps.field.value}
                                                             onChange={(e) => fieldProps.form.setFieldValue('highlightColour', e.value)}
                                                />
                                            )
                                        }}
                                    </MFormFieldControl>
                                </div>
                            </MFormSection>

                            <MFormSection title="Contacts">
                                <div className="col-12 md:col-12">
                                    <ContactListControl parentId={Number(this.props.match.params.id)}
                                                        msalContext={this.props.msalContext}
                                    />
                                </div>
                            </MFormSection>

                            <MFormSection title="Bookings">
                                <div className={"col-12 md:col-12"}>
                                    <CrewJobsControl parentId={Number(this.props.match.params.id)}
                                                     msalContext={this.props.msalContext}
                                    />
                                </div>
                            </MFormSection>
                        </div>
                    </div>

                </MForm>

            </MPage>

        );

    }

    private get isFormDisabled(): boolean{
        return this.isSaving;
    }

    private get isSaving() {
        return (this.state.pageActions === PageActions.Saving);
    }
}

export default withMsal(withRouter(CreateCrewPage));
